/**
 * CouponInput - Promo/coupon code input with expand/collapse behavior
 *
 * Three states:
 * - Collapsed: Shows "Have a promo code?" link
 * - Expanded: Shows input field with "Apply" button
 * - Applied: Shows a discount badge with dismiss button
 */

interface CouponInputProps {
  /** Current coupon value */
  value: string;
  /** Callback when coupon changes */
  onChange: (value: string) => void;
  /** Callback to apply the coupon */
  onApply?: () => void;
  /** Error message from coupon validation */
  error?: string | null;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether the input is expanded (visible) */
  isExpanded?: boolean;
  /** Callback to expand the input */
  onExpand?: () => void;
  /** Whether a coupon has been applied */
  isApplied?: boolean;
  /** Whether coupon is currently being validated */
  isValidating?: boolean;
  /** Applied coupon code (for badge display) */
  couponCode?: string;
  /** Discount percentage (for badge display) */
  discountPercent?: number;
  /** Callback to remove applied coupon */
  onRemoveCoupon?: () => void;
}

export function CouponInput({
  value,
  onChange,
  onApply,
  error,
  disabled,
  isExpanded = true,
  onExpand,
  isApplied = false,
  isValidating = false,
  couponCode,
  discountPercent,
  onRemoveCoupon,
}: CouponInputProps) {
  // When coupon is applied, show the discount badge
  if (isApplied && couponCode && discountPercent != null && discountPercent > 0) {
    return (
      <div>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-green-300 bg-green-50 py-1.5 pr-2.5 pl-2 text-xs font-semibold text-green-600">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <circle cx="7" cy="7" r="6" fill="#22C55E" opacity="0.15" />
            <path d="M4.5 7L6 8.5L9.5 5" stroke="#16A34A" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {couponCode.toUpperCase()}
          <span className="text-green-400">&middot;</span>
          <span className="text-[11px] text-green-700">{discountPercent}% off</span>
          {onRemoveCoupon && (
            <button
              type="button"
              onClick={onRemoveCoupon}
              className="ml-0.5 flex items-center"
              aria-label="Remove coupon"
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path d="M3 3L9 9M9 3L3 9" stroke="#86EFAC" strokeWidth="1.3" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </span>
      </div>
    );
  }

  // Collapsed state: show expandable link
  if (!isExpanded) {
    return (
      <div>
        <button
          type="button"
          onClick={onExpand}
          className="text-xs text-slate-500 underline hover:text-slate-700"
        >
          Have a promo code?
        </button>
      </div>
    );
  }

  // Expanded state: show input with Apply button
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value.trimStart());
  };

  const handleApply = () => {
    onChange(value.trim());
    onApply?.();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApply();
    }
  };

  const hasError = Boolean(error);

  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium text-catch-dark-blue-90">
        Promo Code
      </label>
      <div className="flex gap-2">
        <input
          name="coupon"
          type="text"
          placeholder="Enter promo code"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled || isValidating}
          className={`min-h-[44px] flex-1 rounded-lg bg-white px-3.5 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-catch-avocado-400 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 ${
            hasError ? 'ring-2 ring-red-500' : ''
          }`}
        />
        <button
          type="button"
          onClick={handleApply}
          disabled={disabled || isValidating || !value.trim()}
          className="min-h-[44px] rounded-lg border border-slate-300 bg-white px-4 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isValidating ? (
            <svg className="h-4 w-4 animate-spin text-slate-500" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ) : (
            'Apply'
          )}
        </button>
      </div>
      {hasError && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

export default CouponInput;
